<template>
  <div ref="rootEl" class="listing-results" :class="`listing-results--${view}`" :style="stickyStyle">
    <!-- Error and empty states render inside the list column so the map stays
         mounted and usable; panning to an empty area must not remove the map -->
    <div v-if="view !== 'map'" class="listing-results__list">
      <p v-if="results.error" class="listing-results__state">
        {{ t('listing-toolbar.error') }}: {{ results.error }}
      </p>
      <div
        v-else-if="showEmpty"
        class="listing-results__state listing-results__empty"
      >
        <p class="listing-results__empty-title">{{ t('listing-results.empty.title') }}</p>
        <p class="listing-results__empty-body">{{ t('listing-results.empty.body') }}</p>
      </div>
      <template v-else>
        <div class="listing-results__grid" :aria-busy="results.loading || results.loadingMore">
          <!-- Stale-while-revalidate: a page-1 refresh (map pan/zoom, filter
               change) keeps the previous cards on screen until the new payload
               lands, so the list never flashes to skeletons mid-scroll -->
          <ListingCard v-for="listing in results.results" :key="listing.id" :listing="listing" />
          <div
            v-for="index in skeletonCount"
            :key="`skeleton-${index}`"
            class="listing-card-skeleton"
            aria-hidden="true"
          >
            <div class="listing-card-skeleton__media" />
            <div class="listing-card-skeleton__body">
              <span class="listing-card-skeleton__line listing-card-skeleton__line--title" />
              <span class="listing-card-skeleton__line listing-card-skeleton__line--location" />
              <span class="listing-card-skeleton__line listing-card-skeleton__line--price" />
              <div class="listing-card-skeleton__specs">
                <span v-for="spec in 4" :key="spec" class="listing-card-skeleton__line" />
              </div>
            </div>
          </div>
        </div>
        <div v-if="canLoadMore" ref="sentinel" class="listing-results__sentinel" />
      </template>
    </div>

    <aside v-if="view !== 'list'" class="listing-results__map">
      <ListingMap />
    </aside>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import ListingCard from './ListingCard.vue';
import ListingMap from './ListingMap.vue';
import { t } from '../lib/i18n';
import { useResults } from '../lib/useResults';
import { suppressScrollCollapse } from '../lib/useScrollCollapse';
import { useView } from '../lib/useView';

const emit = defineEmits(['load-more']);

const { results } = useResults();
const { view } = useView();

const INITIAL_SKELETONS = 6;
const APPEND_SKELETONS = 3;

const skeletonCount = computed(() => {
  if (results.loading && !results.results.length) return INITIAL_SKELETONS;
  if (results.loadingMore) return APPEND_SKELETONS;
  return 0;
});

const showEmpty = computed(() => !results.loading && !results.results.length);

// measured bottom of the sticky search header; the map column's sticky top
// and height derive from it in the CSS
const headerBottom = ref(null);

const measureHeader = () => {
  const header = document.querySelector('.listing-sticky-header');
  if (!header) return;
  const top = Number.parseFloat(getComputedStyle(header).top);
  if (Number.isNaN(top)) return;
  headerBottom.value = Math.round(top + header.offsetHeight);
};

const stickyStyle = computed(() =>
  headerBottom.value === null ? {} : { '--listing-header-bottom': `${headerBottom.value}px` }
);

// A new page-1 result set starts at its first card, so a reader who scrolled
// past the top of the results is jumped back up to it. Hard on purpose:
// 'instant' overrides the global css scroll-behavior: smooth, and the sync
// watch below runs this before the DOM swap, so the browser never clamps the
// scroll position against the shorter list (that clamp was the old "spring").
const rootEl = ref(null);

const jumpToResultsTop = () => {
  if (!rootEl.value) return;
  const top =
    window.scrollY + rootEl.value.getBoundingClientRect().top - (headerBottom.value ?? 0);
  if (window.scrollY <= top) return;
  suppressScrollCollapse();
  window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
};

const canLoadMore = computed(
  () => !results.loading && Boolean(results.meta?.hasMore)
);

// prefetch margin: start loading the next page well before the sentinel is
// visible, so the footer does not flash into view before the cards land
const PREFETCH_MARGIN = '600px';

const sentinel = ref(null);
let observer = null;
let layoutObserver = null;

onMounted(() => {
  window.addEventListener('resize', measureHeader);

  // A mount-only measurement goes stale: webfonts load after mount and grow
  // the sticky header, leaving the map column sized and pinned against the
  // wrong header bottom. Observing the header catches that (ResizeObserver
  // also fires once on observe, which is the initial measurement).
  const header = document.querySelector('.listing-sticky-header');
  if (header) {
    layoutObserver = new ResizeObserver(measureHeader);
    layoutObserver.observe(header);
  } else {
    measureHeader();
  }

  // Fires on page-1 replacements only: appends push into the same array and
  // do not swap the reference
  watch(() => results.results, jumpToResultsTop, { flush: 'sync' });
  watch(view, jumpToResultsTop, { flush: 'sync' });

  observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) emit('load-more');
    },
    { rootMargin: `0px 0px ${PREFETCH_MARGIN} 0px` }
  );

  watch(
    sentinel,
    (el) => {
      observer.disconnect();
      if (el) observer.observe(el);
    },
    { immediate: true }
  );
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', measureHeader);
  if (observer) observer.disconnect();
  if (layoutObserver) layoutObserver.disconnect();
});
</script>
